import { Base64, SHA256 } from 'jshashes';
import qsLib from 'qs';

import { generateRandomString } from 'src/lib/crypto';
import { getEnvironment } from 'src/lib/featureFlags';
import { getQueryParams } from 'src/lib/history';
import { sessionStorage } from 'src/lib/localStorage';

const REDIRECT_URI = `${window.location.protocol}//${window.location.host}/integration-jira-ui/pagerduty/oauth/callback`;

const CURITY_REGION_ACR = {
  UnitedStates: 'PagerDutySilentAuthUS',
  Europe: 'PagerDutySilentAuthEU',
};

const postData = (url, body) => fetch(
  url,
  {
    method: 'post',
    cache: 'no-cache',
    headers: {
      Authorization: '',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body
  }
).then(response => response.json());

const verifier = {
  generateVerifier() {
    // account for the overhead of going to base64
    const bytes = Math.floor(128 / 1.37);
    let randomArray = new Uint16Array(bytes);

    randomArray = crypto.getRandomValues(randomArray);
    const randomString = String.fromCharCode.apply(
      null,
      randomArray
    );

    // maximum length should be 128
    // slice could do this too
    // then we could use a larger random value
    // and shrink it
    const base64 = new Base64();
    return base64.encode(randomString)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  },

  generateChallenge(verifier) {
    const sha = new SHA256();
    return sha.b64(verifier)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }
};

// Step 1: Request AuthorizationCode
const requestAuthorizationCode = (subdomain) => {
  const { silentOauthClientId, curityRegion, identityBaseUrl } = getEnvironment();

  const state = generateRandomString(40);
  sessionStorage.setItem('integration_jira_oauth_state', state);
  sessionStorage.setItem('integration_jira_oauth_resume_uri', window.location.href);

  const codeVerifier = verifier.generateVerifier();
  const codeChallenge = verifier.generateChallenge(codeVerifier);
  const codeChallengeMethod = 'S256';

  sessionStorage.setItem('integration_jira_oauth_code_verifier', codeVerifier);

  const params = {
    client_id: silentOauthClientId,
    redirect_uri: REDIRECT_URI,
    state,
    scope: 'openid write',
    response_type: 'code',
    code_challenge_method: codeChallengeMethod,
    code_challenge: codeChallenge,
    subdomain,
    acr: CURITY_REGION_ACR[curityRegion],
  };

  const oAuthUrl = `${identityBaseUrl}/oauth/authorize?${qsLib.stringify(params)}`;

  // eslint-disable-next-line require-atomic-updates
  window.location.href = oAuthUrl;
};

// Step 2: Exchange Authorization Code for AccessToken
async function exchangeAuthorizationCodeForAccessToken(code, state) {
  if (state !== sessionStorage.getItem('integration_jira_oauth_state')) {
    throw new Error("The state doesn't match.");
  }

  const {
    silentOauthClientId,
    curityRegion,
    appBaseUrl,
    apiBaseUrl,
    accountBaseUrlTemplate,
    identityBaseUrl
  } = getEnvironment();
  const codeVerifier = sessionStorage.getItem('integration_jira_oauth_code_verifier');

  const params = {
    grant_type: 'authorization_code',
    client_id: silentOauthClientId,
    redirect_uri: REDIRECT_URI,
    code,
    state,
    code_verifier: codeVerifier
  };

  const tokenUrl = `${identityBaseUrl}/oauth/token`;
  const body = qsLib.stringify(params);

  const { access_token, id_token, error, error_description } = await postData(tokenUrl, body);

  if (!access_token) {
    throw new Error(error_description || error || 'Invalid response');
  }

  if (!id_token) {
    throw new Error('Auth service did not respond with id_token');
  }

  const [, base46body] = id_token.split('.');
  const decodedBody = atob(base46body);
  const idToken = JSON.parse(decodedBody);

  if (idToken.region !== curityRegion) {
    throw new Error(`Can not login to another region.
    This integration works only with ${curityRegion} accounts,
    but you provided credentials for ${idToken.region} account.`.replaceAll('\n', ''));
  }

  const credentials = {
    accessToken: access_token,
    accountDomain: idToken.subdomain,
    environment: {
      appBaseUrl,
      apiBaseUrl,
      accountBaseUrlTemplate,
    }
  };

  return credentials;
}

function redirectOnSuccess() {
  window.location.href = sessionStorage.getItem('integration_jira_oauth_resume_uri');
}

function cleanupSessionStorage() {
  sessionStorage.setItem('integration_jira_oauth_code_verifier', undefined);
  sessionStorage.setItem('integration_jira_oauth_state', undefined);
  sessionStorage.setItem('integration_jira_oauth_resume_uri', undefined);
}

export async function processSilentOAuth() {
  let session = sessionStorage.getItem('integration_jira_session');
  if (session) return session;

  const { code, state, subdomain, error, error_description } = getQueryParams();

  let updated_error_description = error_description;
  if (error || updated_error_description) {
    // Step 1 had failed
    // Check if error_description contains 'Try again.' and update it to 'Please refresh the page and try again.'
    if (updated_error_description && updated_error_description.includes('Try again.')) {
      updated_error_description = updated_error_description.replace('Try again.', 'Please refresh the page and try again.');
    }
    throw new Error(updated_error_description || error);
  }

  if (code) {
    // Step 2: Obtain the Access Grant
    // Exchange Authorization Code for the AccessToken
    session = await exchangeAuthorizationCodeForAccessToken(code, state);
    sessionStorage.setItem('integration_jira_session', session);
    redirectOnSuccess();
    cleanupSessionStorage();
    return session;
  } else if (subdomain) {
    // Step 1: Initiate the Access Grant
    // Redirect to get the Authorization Code
    requestAuthorizationCode(subdomain);
    return new Promise(() => { });
  } else {
    throw new Error('Subdomain is required for authentication');
  }
}
